import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from 'lucide-react';
import AdminShell from '../../admin/components/layout/AdminShell';
import AdminPageHeader from '../../admin/components/shared/AdminPageHeader';
import AdminPanel from '../../admin/components/shared/AdminPanel';
import PrimaryActionButton from '../../admin/components/buttons/PrimaryActionButton';
import SecondaryActionButton from '../../admin/components/buttons/SecondaryActionButton';
import DangerActionButton from '../../admin/components/buttons/DangerActionButton';
import GhostActionButton from '../../admin/components/buttons/GhostActionButton';
import TextField from '../../admin/components/fields/TextField';
import TextareaField from '../../admin/components/fields/TextareaField';
import ToggleField from '../../admin/components/fields/ToggleField';
import ImageUploadField from '../../admin/components/fields/ImageUploadField';
import EmptyStatePanel from '../../admin/components/shared/EmptyStatePanel';
import SmartImagePreview from '../../components/shared/SmartImagePreview';
import { useAdminContext } from '../../context/AdminContext';
import { formatPrice } from '../../utils/restaurantBranding';
import { createEmptyCategory, reorderList, sortCategories, syncCategorySlug } from '../../utils/workspaceHelpers';

const AdminCategoriesPage: React.FC = () => {
  const { draftWorkspace, draftStatus, draftError, updateWorkspace, saveDraft, saving, uploadImage } =
    useAdminContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const categories = useMemo(
    () => (draftWorkspace ? sortCategories(draftWorkspace.categories) : []),
    [draftWorkspace],
  );

  useEffect(() => {
    if (!selectedCategoryId || !categories.some((category) => category.id === selectedCategoryId)) {
      setSelectedCategoryId(categories[0]?.id ?? null);
    }
  }, [categories, selectedCategoryId]);

  const selectedCategory = categories.find((category) => category.id === selectedCategoryId);
  const linkedItems = useMemo(
    () =>
      draftWorkspace?.items.filter((item) => item.categoryId === selectedCategoryId).sort((left, right) => left.sortOrder - right.sortOrder) ?? [],
    [draftWorkspace, selectedCategoryId],
  );

  const categoryAssets = draftWorkspace?.mediaAssets.filter((asset) => asset.kind !== 'item-image') ?? [];

  const updateCategory = (updater: (category: NonNullable<typeof selectedCategory>) => NonNullable<typeof selectedCategory>) => {
    if (!selectedCategory) {
      return;
    }

    updateWorkspace((workspace) => ({
      ...workspace,
      categories: sortCategories(workspace.categories).map((category) =>
        category.id === selectedCategory.id ? updater(category) : category,
      ),
    }));
  };

  const handleAddCategory = () => {
    const nextCategory = {
      ...createEmptyCategory(),
      sortOrder: categories.length,
    };

    updateWorkspace((workspace) => ({
      ...workspace,
      categories: [...sortCategories(workspace.categories), nextCategory],
    }));
    setSelectedCategoryId(nextCategory.id);
    setPageError(null);
  };

  const handleDeleteCategory = () => {
    if (!selectedCategory || !draftWorkspace) {
      return;
    }

    const itemsInCategory = draftWorkspace.items.filter((item) => item.categoryId === selectedCategory.id);
    if (itemsInCategory.length > 0) {
      const confirmed = window.confirm(
        `Delete "${selectedCategory.title}" and all ${itemsInCategory.length} menu item(s) in it? This removes those items from the draft.`,
      );
      if (!confirmed) {
        return;
      }
    }

    updateWorkspace((workspace) => {
      const nextCategories = sortCategories(workspace.categories)
        .filter((category) => category.id !== selectedCategory.id)
        .map((category, index) => ({
          ...category,
          sortOrder: index,
        }));

      const survivingItems = workspace.items.filter((item) => item.categoryId !== selectedCategory.id);

      const reindexedItems = nextCategories.flatMap((category) => {
        const forCategory = survivingItems
          .filter((item) => item.categoryId === category.id)
          .sort((left, right) => left.sortOrder - right.sortOrder);
        return forCategory.map((item, index) => ({
          ...item,
          sortOrder: index,
        }));
      });

      return {
        ...workspace,
        categories: nextCategories,
        items: reindexedItems,
      };
    });
    setSelectedCategoryId(null);
    setPageError(null);
  };

  const moveSelectedCategory = (direction: -1 | 1) => {
    if (!selectedCategory) {
      return;
    }

    const currentIndex = categories.findIndex((category) => category.id === selectedCategory.id);
    const targetIndex = currentIndex + direction;

    if (targetIndex < 0 || targetIndex >= categories.length) {
      return;
    }

    updateWorkspace((workspace) => ({
      ...workspace,
      categories: reorderList(sortCategories(workspace.categories), currentIndex, targetIndex),
    }));
  };

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow="Menu Categories"
        title="Manage menu sections and cover images"
        description="Create, reorder, hide, and refine the categories that shape the public menu experience."
        actions={
          <>
            <SecondaryActionButton onClick={() => void saveDraft()} disabled={saving || draftStatus !== 'ready'}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Draft'}
            </SecondaryActionButton>
            <PrimaryActionButton onClick={handleAddCategory}>
              <Plus className="h-4 w-4" />
              Add Category
            </PrimaryActionButton>
          </>
        }
      />

      {(pageError || draftError) && (
        <AdminPanel className="p-4">
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {pageError || draftError}
          </div>
        </AdminPanel>
      )}

      {!draftWorkspace || draftStatus === 'loading' ? (
        <AdminPanel className="p-8 text-center text-white/60">Loading categories...</AdminPanel>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]">
          <AdminPanel className="p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Category List</div>
                <div className="mt-2 text-lg font-semibold text-white">{categories.length} categories</div>
              </div>
            </div>

            <div className="space-y-3">
              {categories.map((category) => {
                const itemCount = draftWorkspace.items.filter((item) => item.categoryId === category.id).length;
                const isSelected = category.id === selectedCategoryId;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setSelectedCategoryId(category.id)}
                    className={`w-full rounded-[24px] border px-4 py-4 text-left transition ${
                      isSelected
                        ? 'border-amber-300/35 bg-amber-400/12'
                        : 'border-white/10 bg-white/5 hover:bg-white/8'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <SmartImagePreview src={category.coverImage} alt={category.title} className="h-20 w-20 shrink-0" fit="cover" />
                      <div className="min-w-0 flex-1">
                        <div className="text-lg font-semibold text-white">{category.title}</div>
                        <div className="mt-1 text-sm text-white/45">{itemCount} items</div>
                        {category.shortDescription && (
                          <div className="mt-2 line-clamp-2 text-sm leading-6 text-white/45">{category.shortDescription}</div>
                        )}
                        {itemCount === 0 && (
                          <div className="mt-2 inline-flex rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-amber-100">
                            Empty category
                          </div>
                        )}
                      </div>
                      <div className="arab-price-pill shrink-0">{category.isVisible ? 'Visible' : 'Hidden'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </AdminPanel>

          {!selectedCategory ? (
            <EmptyStatePanel title="Select a category" description="Choose a category from the left to edit its content and cover image." />
          ) : (
            <AdminPanel className="p-6">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Category Editor</div>
                  <div className="mt-2 text-2xl font-semibold text-white">{selectedCategory.title}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <GhostActionButton onClick={() => moveSelectedCategory(-1)} disabled={categories[0]?.id === selectedCategory.id}>
                    <ChevronUp className="h-4 w-4" />
                  </GhostActionButton>
                  <GhostActionButton onClick={() => moveSelectedCategory(1)} disabled={categories[categories.length - 1]?.id === selectedCategory.id}>
                    <ChevronDown className="h-4 w-4" />
                  </GhostActionButton>
                  <DangerActionButton onClick={handleDeleteCategory}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DangerActionButton>
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <TextField
                  label="Title"
                  value={selectedCategory.title}
                  onChange={(value) =>
                    updateCategory((category) =>
                      syncCategorySlug({
                        ...category,
                        title: value,
                        slug:
                          category.slug === category.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                            ? value
                            : category.slug,
                      }),
                    )
                  }
                  required
                />
                <TextField
                  label="Slug"
                  value={selectedCategory.slug}
                  onChange={(value) => updateCategory((category) => syncCategorySlug({ ...category, slug: value }))}
                  helpText="Used for filtering and future deep links."
                />
              </div>

              <div className="mt-5">
                <TextareaField
                  label="Short Description"
                  value={selectedCategory.shortDescription}
                  onChange={(value) => updateCategory((category) => ({ ...category, shortDescription: value }))}
                  placeholder="What makes this category distinct?"
                />
              </div>

              <div className="mt-5">
                <ToggleField
                  label="Visible on Public Menu"
                  value={selectedCategory.isVisible}
                  onChange={(value) => updateCategory((category) => ({ ...category, isVisible: value }))}
                  description="Hidden categories remain in draft data but disappear from the customer-facing site."
                />
              </div>

              <div className="mt-5">
                <ImageUploadField
                  label="Category Cover Image"
                  value={selectedCategory.coverImage}
                  onChange={(value) => updateCategory((category) => ({ ...category, coverImage: value }))}
                  assets={categoryAssets}
                  onUpload={(file) => uploadImage(file, 'category-cover', selectedCategory.title)}
                  helpText="Upload from the browser or reuse an existing media asset."
                />
              </div>

              <div className="mt-6 rounded-[24px] border border-white/10 bg-white/5 p-4">
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Items in this category</div>
                <div className="mt-3 space-y-2">
                  {linkedItems.length === 0 ? (
                    <div className="text-sm text-white/50">No items are assigned here yet.</div>
                  ) : (
                    linkedItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-white/8 bg-[#120807] px-4 py-3">
                        <SmartImagePreview
                          src={item.imageUrl || selectedCategory.coverImage}
                          alt={item.imageAlt || item.name}
                          className="h-16 w-16 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-white">{item.name}</div>
                          <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/40">
                            {item.isVisible ? 'Visible' : 'Hidden'} · {item.isAvailable ? 'Available' : 'Unavailable'}
                          </div>
                        </div>
                        <div className="arab-price-pill">{formatPrice(item.price, draftWorkspace.settings)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </AdminPanel>
          )}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminCategoriesPage;
